import React, { useState, useEffect } from 'react';
import { 
  Building2, Users, Shield, Search, Filter, MoreVertical,
  Check, X, Loader2, AlertTriangle, Crown, Zap, Rocket,
  Calendar, MapPin, ChevronDown, Eye, Edit2, Power, CreditCard,
  RefreshCw, UserPlus, Globe, DollarSign, Receipt, ArrowUpDown,
  Send, Clock, CheckCircle2
} from 'lucide-react';
import { format } from 'date-fns';
import { api } from '../../services/api';
import PlatformLayout from '../../components/layout/PlatformLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const planConfig = {
  basic: { icon: Crown, color: 'bg-slate-500', label: 'Basic', textColor: 'text-slate-300' },
  pro: { icon: Zap, color: 'bg-blue-500', label: 'Pro', textColor: 'text-blue-300' },
  enterprise: { icon: Rocket, color: 'bg-violet-500', label: 'Enterprise', textColor: 'text-violet-300' }
};

export default function PlatformOrganizations() {
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterPlan, setFilterPlan] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Modal states
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [orgDetails, setOrgDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  
  // Edit modal states
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);
  const [newPlan, setNewPlan] = useState('');
  const [newLimit, setNewLimit] = useState('');
  const [saving, setSaving] = useState(false);

  // Billing modal states
  const [billingModalOpen, setBillingModalOpen] = useState(false);
  const [billingOrg, setBillingOrg] = useState(null);
  const [billingData, setBillingData] = useState(null);
  const [loadingBilling, setLoadingBilling] = useState(false);
  const [subAmount, setSubAmount] = useState('');
  const [subInterval, setSubInterval] = useState('month');
  const [subDescription, setSubDescription] = useState('');
  const [adjustAmount, setAdjustAmount] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  const [billingAction, setBillingAction] = useState(null); // 'create' | 'adjust'
  const [billingResult, setBillingResult] = useState(null);

  useEffect(() => {
    loadOrganizations();
  }, []);

  const loadOrganizations = async () => {
    try {
      setLoading(true);
      const data = await api.getPlatformOrganizations();
      setOrganizations(data.organizations || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadOrgDetails = async (orgId) => {
    try {
      setLoadingDetails(true);
      const data = await api.getPlatformOrganization(orgId);
      setOrgDetails(data);
    } catch (err) {
      console.error('Error loading org details:', err);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleViewDetails = (org) => {
    setSelectedOrg(org);
    setDetailsModalOpen(true);
    loadOrgDetails(org.id);
  };

  const handleEditOrg = (org) => {
    setEditingOrg(org);
    setNewPlan(org.plan || 'basic');
    setNewLimit(org.manual_guard_limit?.toString() || '');
    setEditModalOpen(true);
  };

  const handleSaveChanges = async () => {
    if (!editingOrg) return;
    
    try {
      setSaving(true);
      
      // Update plan if changed
      if (newPlan !== editingOrg.plan) {
        await api.updateOrganizationPlan(editingOrg.id, newPlan);
      }
      
      // Update limit if provided
      if (newLimit && newLimit !== editingOrg.manual_guard_limit?.toString()) {
        await api.updateOrganizationLimits(editingOrg.id, { 
          manual_guard_limit: parseInt(newLimit) 
        });
      }
      
      setEditModalOpen(false);
      loadOrganizations();
    } catch (err) {
      alert('Error saving changes: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (org) => {
    if (!window.confirm(`Are you sure you want to ${org.is_active ? 'deactivate' : 'activate'} ${org.name}?`)) {
      return;
    }
    
    try {
      await api.updateOrganizationStatus(org.id, !org.is_active);
      loadOrganizations();
    } catch (err) {
      alert('Error updating status: ' + err.message);
    }
  };

  const handleManageBilling = async (org) => {
    setBillingOrg(org);
    setBillingModalOpen(true);
    setBillingAction(null);
    setBillingResult(null);
    setSubAmount(org.subscription_amount?.toString() || org.custom_monthly_price?.toString() || '');
    setSubInterval(org.subscription_interval || 'month');
    setSubDescription('Monthly Security Platform Subscription');
    setAdjustAmount(org.subscription_amount?.toString() || org.custom_monthly_price?.toString() || '');
    setAdjustReason('');
    try {
      setLoadingBilling(true);
      const data = await api.getOrganizationBilling(org.id);
      setBillingData(data);
    } catch (err) {
      console.error('Error loading billing:', err);
    } finally {
      setLoadingBilling(false);
    }
  };

  const handleCreateSubscription = async () => {
    if (!subAmount || parseFloat(subAmount) <= 0) return;
    try {
      setSaving(true);
      const result = await api.createPaymentLink(billingOrg.id, parseFloat(subAmount), subDescription, subInterval);
      setBillingResult(result);
      setBillingAction(null);
      // Reload billing data
      const data = await api.getOrganizationBilling(billingOrg.id);
      setBillingData(data);
      loadOrganizations();
    } catch (err) {
      alert('Error creating subscription: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleAdjustSubscription = async () => {
    if (!adjustAmount || parseFloat(adjustAmount) <= 0) return;
    try {
      setSaving(true);
      const result = await api.updateSubscription(billingOrg.id, parseFloat(adjustAmount), adjustReason, subInterval);
      setBillingResult(result);
      setBillingAction(null);
      // Reload billing data
      const data = await api.getOrganizationBilling(billingOrg.id);
      setBillingData(data);
      loadOrganizations();
    } catch (err) {
      alert('Error adjusting subscription: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Filter organizations
  const filteredOrgs = organizations.filter(org => {
    const matchesSearch = org.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         org.slug?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesPlan = filterPlan === 'all' || org.plan === filterPlan;
    const matchesStatus = filterStatus === 'all' || 
                         (filterStatus === 'active' && org.is_active) ||
                         (filterStatus === 'inactive' && !org.is_active);
    return matchesSearch && matchesPlan && matchesStatus;
  });

  if (loading) {
    return (
      <PlatformLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <Loader2 className="w-10 h-10 animate-spin text-violet-500 mx-auto" />
            <p className="mt-3 text-slate-400">Loading organizations...</p>
          </div>
        </div>
      </PlatformLayout>
    );
  }

  if (error) {
    return (
      <PlatformLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Failed to Load</h2>
            <p className="text-slate-400 mb-4">{error}</p>
            <Button onClick={loadOrganizations} className="bg-violet-600 hover:bg-violet-700">
              Try Again
            </Button>
          </div>
        </div>
      </PlatformLayout>
    );
  }

  return (
    <PlatformLayout>
      <div className="space-y-6" data-testid="platform-organizations">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white">Organizations</h1>
            <p className="text-slate-400 mt-1">{organizations.length} total organizations</p>
          </div>
          <Button onClick={loadOrganizations} variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-700">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Filters */}
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search organizations..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-slate-700 border-slate-600 text-white"
                />
              </div>
              <Select value={filterPlan} onValueChange={setFilterPlan}>
                <SelectTrigger className="w-[150px] bg-slate-700 border-slate-600 text-white">
                  <SelectValue placeholder="Plan" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="all">All Plans</SelectItem>
                  <SelectItem value="basic">Basic</SelectItem>
                  <SelectItem value="pro">Pro</SelectItem>
                  <SelectItem value="enterprise">Enterprise</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-[150px] bg-slate-700 border-slate-600 text-white">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Organizations Grid */}
        {filteredOrgs.length === 0 ? (
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-12 text-center">
              <Building2 className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">No Organizations Found</h3>
              <p className="text-slate-400">
                {searchQuery || filterPlan !== 'all' || filterStatus !== 'all'
                  ? 'Try adjusting your filters'
                  : 'Organizations will appear here when they sign up'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {filteredOrgs.map((org) => {
              const plan = planConfig[org.plan] || planConfig.basic;
              const PlanIcon = plan.icon;
              
              return (
                <Card key={org.id} className={`bg-slate-800 border-slate-700 hover:border-slate-600 transition-all ${!org.is_active && 'opacity-60'}`}>
                  <CardContent className="p-6">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 ${plan.color} rounded-xl flex items-center justify-center`}>
                          <Building2 className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-white truncate max-w-[180px]">{org.name}</h3>
                          <p className="text-sm text-slate-400">@{org.slug}</p>
                        </div>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="text-slate-400 hover:text-white">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-slate-800 border-slate-700">
                          <DropdownMenuItem onClick={() => handleViewDetails(org)} className="text-slate-300 focus:bg-slate-700">
                            <Eye className="w-4 h-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleEditOrg(org)} className="text-slate-300 focus:bg-slate-700">
                            <Edit2 className="w-4 h-4 mr-2" />
                            Edit Plan & Limits
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleManageBilling(org)} className="text-slate-300 focus:bg-slate-700">
                            <CreditCard className="w-4 h-4 mr-2" />
                            Manage Billing
                          </DropdownMenuItem>
                          <DropdownMenuSeparator className="bg-slate-700" />
                          <DropdownMenuItem onClick={() => handleToggleStatus(org)} className={org.is_active ? 'text-red-400 focus:bg-slate-700' : 'text-green-400 focus:bg-slate-700'}>
                            <Power className="w-4 h-4 mr-2" />
                            {org.is_active ? 'Deactivate' : 'Activate'}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>

                    {/* Status & Plan */}
                    <div className="flex items-center gap-2 mb-4">
                      <Badge className={`${plan.color} text-white`}>
                        <PlanIcon className="w-3 h-3 mr-1" />
                        {plan.label}
                      </Badge>
                      <Badge variant={org.is_active ? 'default' : 'secondary'} className={org.is_active ? 'bg-green-500/20 text-green-400 border-green-500/30' : 'bg-slate-600 text-slate-400'}>
                        {org.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      <div className="bg-slate-700/50 rounded-lg p-3 text-center">
                        <Users className="w-5 h-5 text-blue-400 mx-auto mb-1" />
                        <p className="text-lg font-bold text-white">{org.guard_count || 0}</p>
                        <p className="text-xs text-slate-400">Guards</p>
                      </div>
                      <div className="bg-slate-700/50 rounded-lg p-3 text-center">
                        <MapPin className="w-5 h-5 text-green-400 mx-auto mb-1" />
                        <p className="text-lg font-bold text-white">{org.site_count || 0}</p>
                        <p className="text-xs text-slate-400">Sites</p>
                      </div>
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-between text-sm text-slate-400 pt-3 border-t border-slate-700">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {org.created_at ? format(new Date(org.created_at), 'MMM d, yyyy') : 'N/A'}
                      </div>
                      {org.manual_guard_limit && (
                        <div className="flex items-center gap-1 text-amber-400">
                          <Shield className="w-4 h-4" />
                          Limit: {org.manual_guard_limit}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Details Modal */}
      <Dialog open={detailsModalOpen} onOpenChange={setDetailsModalOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className={`w-10 h-10 ${planConfig[selectedOrg?.plan]?.color || 'bg-slate-500'} rounded-xl flex items-center justify-center`}>
                <Building2 className="w-5 h-5 text-white" />
              </div>
              {selectedOrg?.name}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Organization ID: {selectedOrg?.id}
            </DialogDescription>
          </DialogHeader>

          {loadingDetails ? (
            <div className="py-8 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-violet-500 mx-auto" />
              <p className="text-slate-400 mt-2">Loading details...</p>
            </div>
          ) : orgDetails ? (
            <div className="space-y-6">
              {/* Organization Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-700/50 rounded-lg p-4">
                  <p className="text-sm text-slate-400 mb-1">Plan</p>
                  <p className="text-white font-medium capitalize">{orgDetails.organization?.plan}</p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-4">
                  <p className="text-sm text-slate-400 mb-1">Status</p>
                  <p className={`font-medium ${orgDetails.organization?.is_active ? 'text-green-400' : 'text-red-400'}`}>
                    {orgDetails.organization?.is_active ? 'Active' : 'Inactive'}
                  </p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-4">
                  <p className="text-sm text-slate-400 mb-1">Invite Code</p>
                  <p className="text-white font-mono">{orgDetails.organization?.invite_code || 'N/A'}</p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-4">
                  <p className="text-sm text-slate-400 mb-1">Guard Limit</p>
                  <p className="text-white">
                    {orgDetails.organization?.manual_guard_limit || 'Plan default'}
                  </p>
                </div>
              </div>

              {/* Users */}
              <div>
                <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Users ({orgDetails.users_count || 0})
                </h4>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {orgDetails.users?.map((user) => (
                    <div key={user.id} className="flex items-center justify-between bg-slate-700/50 rounded-lg p-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                          user.role === 'admin' ? 'bg-violet-500' :
                          user.role === 'supervisor' ? 'bg-blue-500' : 'bg-green-500'
                        }`}>
                          {user.name?.[0]?.toUpperCase() || '?'}
                        </div>
                        <div>
                          <p className="text-white font-medium">{user.name}</p>
                          <p className="text-sm text-slate-400">{user.email}</p>
                        </div>
                      </div>
                      <Badge variant="outline" className="capitalize border-slate-600 text-slate-300">
                        {user.role}
                      </Badge>
                    </div>
                  ))}
                  {(!orgDetails.users || orgDetails.users.length === 0) && (
                    <p className="text-slate-400 text-center py-4">No users found</p>
                  )}
                </div>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Edit Organization</DialogTitle>
            <DialogDescription className="text-slate-400">
              Update plan and limits for {editingOrg?.name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-slate-300 mb-2 block">Plan</label>
              <Select value={newPlan} onValueChange={setNewPlan}>
                <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="free">Free</SelectItem>
                  <SelectItem value="starter">Starter</SelectItem>
                  <SelectItem value="basic">Basic</SelectItem>
                  <SelectItem value="pro">Pro</SelectItem>
                  <SelectItem value="enterprise">Enterprise</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium text-slate-300 mb-2 block">
                Manual Guard Limit (optional override)
              </label>
              <Input
                type="number"
                placeholder="Leave empty for plan default"
                value={newLimit}
                onChange={(e) => setNewLimit(e.target.value)}
                className="bg-slate-700 border-slate-600 text-white"
              />
              <p className="text-xs text-slate-400 mt-1">
                Set a custom guard limit regardless of plan
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModalOpen(false)} className="border-slate-600 text-slate-300">
              Cancel
            </Button>
            <Button onClick={handleSaveChanges} disabled={saving} className="bg-violet-600 hover:bg-violet-700">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Billing Modal */}
      <Dialog open={billingModalOpen} onOpenChange={setBillingModalOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-green-400" />
              Billing - {billingOrg?.name}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Manage subscription and billing for this organization
            </DialogDescription>
          </DialogHeader>

          {loadingBilling ? (
            <div className="py-8 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-violet-500 mx-auto" />
            </div>
          ) : (
            <div className="space-y-6" data-testid="billing-modal">
              {/* Current Subscription Status */}
              <div className="bg-slate-700/50 rounded-xl p-5">
                <h4 className="text-sm font-medium text-slate-400 mb-3">Current Subscription</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-slate-500">Amount</p>
                    <p className="text-2xl font-bold text-white">
                      ${billingData?.subscription_amount || 0}
                      <span className="text-sm text-slate-400 font-normal">/{billingData?.subscription_interval || 'month'}</span>
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Status</p>
                    <Badge className={
                      billingData?.subscription_status === 'active' ? 'bg-green-500/20 text-green-400' :
                      billingData?.subscription_status === 'pending_payment' ? 'bg-amber-500/20 text-amber-400' :
                      'bg-slate-600 text-slate-300'
                    }>
                      {billingData?.subscription_status || 'No subscription'}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Current Guards</p>
                    <p className="text-lg text-white font-semibold">{billingData?.current_guards || 0}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Guard Limit</p>
                    <p className="text-lg text-white font-semibold">{billingData?.guard_limit || 'Plan default'}</p>
                  </div>
                </div>
              </div>

              {/* Success Result */}
              {billingResult && (
                <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-400 mt-0.5" />
                    <div>
                      <p className="text-green-300 font-medium">{billingResult.message}</p>
                      {billingResult.payment_link && (
                        <div className="mt-2">
                          <p className="text-xs text-slate-400 mb-1">Payment Link:</p>
                          <code className="text-xs bg-slate-700 px-2 py-1 rounded text-green-300 break-all block">
                            {billingResult.payment_link}
                          </code>
                          {billingResult.email && (
                            <p className="text-xs text-slate-400 mt-2">
                              Send to: <span className="text-white">{billingResult.email}</span>
                            </p>
                          )}
                        </div>
                      )}
                      {billingResult.old_amount !== undefined && (
                        <p className="text-sm text-slate-300 mt-1">
                          ${billingResult.old_amount} → ${billingResult.new_amount}/{billingResult.interval}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              {!billingAction && (
                <div className="flex gap-3">
                  <Button
                    onClick={() => { setBillingAction('create'); setBillingResult(null); }}
                    className="bg-green-600 hover:bg-green-700 flex-1"
                    data-testid="create-subscription-btn"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Create Subscription Link
                  </Button>
                  <Button
                    onClick={() => { setBillingAction('adjust'); setBillingResult(null); }}
                    className="bg-blue-600 hover:bg-blue-700 flex-1"
                    disabled={!billingData?.subscription_amount}
                    data-testid="adjust-subscription-btn"
                  >
                    <ArrowUpDown className="w-4 h-4 mr-2" />
                    Adjust Amount
                  </Button>
                </div>
              )}

              {/* Create Subscription Form */}
              {billingAction === 'create' && (
                <div className="bg-slate-700/50 rounded-xl p-5 space-y-4">
                  <h4 className="font-medium text-white flex items-center gap-2">
                    <Send className="w-4 h-4 text-green-400" />
                    Create Subscription Link
                  </h4>
                  <p className="text-sm text-slate-400">
                    This creates a recurring payment link to send to the organization admin.
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-slate-400 mb-1 block">Amount (USD)</label>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="49.00"
                        value={subAmount}
                        onChange={(e) => setSubAmount(e.target.value)}
                        className="bg-slate-700 border-slate-600 text-white"
                        data-testid="sub-amount-input"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-slate-400 mb-1 block">Billing Interval</label>
                      <Select value={subInterval} onValueChange={setSubInterval}>
                        <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-slate-700">
                          <SelectItem value="month">Monthly</SelectItem>
                          <SelectItem value="year">Yearly</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Description</label>
                    <Input
                      placeholder="Monthly Security Platform Subscription"
                      value={subDescription}
                      onChange={(e) => setSubDescription(e.target.value)}
                      className="bg-slate-700 border-slate-600 text-white"
                    />
                  </div>
                  <div className="flex gap-3 pt-2">
                    <Button onClick={() => setBillingAction(null)} variant="outline" className="border-slate-600 text-slate-300">
                      Cancel
                    </Button>
                    <Button onClick={handleCreateSubscription} disabled={saving || !subAmount} className="bg-green-600 hover:bg-green-700">
                      {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                      Create & Send Link
                    </Button>
                  </div>
                </div>
              )}

              {/* Adjust Subscription Form */}
              {billingAction === 'adjust' && (
                <div className="bg-slate-700/50 rounded-xl p-5 space-y-4">
                  <h4 className="font-medium text-white flex items-center gap-2">
                    <ArrowUpDown className="w-4 h-4 text-blue-400" />
                    Adjust Subscription
                  </h4>
                  <p className="text-sm text-slate-400">
                    Update the subscription amount (e.g. when the organization adds more guards).
                  </p>
                  <div className="bg-slate-600/50 rounded-lg p-3">
                    <p className="text-xs text-slate-400">Current amount</p>
                    <p className="text-white font-semibold">${billingData?.subscription_amount || 0}/{billingData?.subscription_interval || 'month'}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-slate-400 mb-1 block">New Amount (USD)</label>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="79.00"
                        value={adjustAmount}
                        onChange={(e) => setAdjustAmount(e.target.value)}
                        className="bg-slate-700 border-slate-600 text-white"
                        data-testid="adjust-amount-input"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-slate-400 mb-1 block">Interval</label>
                      <Select value={subInterval} onValueChange={setSubInterval}>
                        <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-slate-700">
                          <SelectItem value="month">Monthly</SelectItem>
                          <SelectItem value="year">Yearly</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Reason for adjustment</label>
                    <Input
                      placeholder="e.g. Added 5 new guards"
                      value={adjustReason}
                      onChange={(e) => setAdjustReason(e.target.value)}
                      className="bg-slate-700 border-slate-600 text-white"
                    />
                  </div>
                  <div className="flex gap-3 pt-2">
                    <Button onClick={() => setBillingAction(null)} variant="outline" className="border-slate-600 text-slate-300">
                      Cancel
                    </Button>
                    <Button onClick={handleAdjustSubscription} disabled={saving || !adjustAmount} className="bg-blue-600 hover:bg-blue-700">
                      {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ArrowUpDown className="w-4 h-4 mr-2" />}
                      Adjust Subscription
                    </Button>
                  </div>
                </div>
              )}

              {/* Billing History */}
              {billingData?.billing_history?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-slate-400 mb-3 flex items-center gap-2">
                    <Receipt className="w-4 h-4" /> Billing History
                  </h4>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {billingData.billing_history.map((entry) => (
                      <div key={entry.id} className="bg-slate-700/50 rounded-lg p-3 flex items-center justify-between">
                        <div>
                          <p className="text-sm text-white">
                            ${entry.old_amount} → ${entry.new_amount}
                            {entry.reason && <span className="text-slate-400"> - {entry.reason}</span>}
                          </p>
                          <p className="text-xs text-slate-500">
                            By {entry.adjusted_by_name} on {entry.created_at ? format(new Date(entry.created_at), 'MMM d, yyyy HH:mm') : 'N/A'}
                          </p>
                        </div>
                        <Badge className="bg-blue-500/20 text-blue-300">Adjusted</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </PlatformLayout>
  );
}
